/**
 * The AuthClient class manages authentication operations, including critical and non-critical functions.
 * Critical functions are essential for app functionality or security and should use `handleError` for error handling.
 * Non-critical functions handle recoverable or less impactful operations and should fail gracefully.
 *
 * The AuthClient now supports a callback for handling critical errors, such as 500 responses,
 * which can be used to trigger app-specific error handling like disaster recovery mode.
 *
 * Summary Table:
 * ┌───────────────────────────────┬───────────┬──────────────────────────────────────────────────────────────┐
 * │ Function                      │ Critical  │ Description                                                  │
 * ├───────────────────────────────┼───────────┼──────────────────────────────────────────────────────────────┤
 * │ _ensureConfigured             │ Yes       │ Ensures the client is configured before use.                 │
 * │ register                      │ Yes       │ Registers a client with the OAuth2 server.                   │
 * │ authorize                     │ Yes       │ Initiates the authorization process.                         │
 * │ authorizeWithToken            │ Yes       │ Authorizes using a One Time Token (OTT) or Deeplink.         │
 * │ exchangeCodeForToken          │ Yes       │ Exchanges an authorization code for tokens.                  │
 * │ refreshIdToken                │ Yes       │ Refreshes the ID token using the refresh token.              │
 * │ smartRefresh                  │ Yes       │ Refreshes the ID token if it is about to expire.             │
 * │ signOut                       │ Yes       │ Signs the user out and clears the session.                   │
 * │ configure                     │ Yes       │ Configures the AuthClient with endpoint and redirect URI.    │
 * │ getAuthStorage                │ No        │ Retrieves a value from local or session storage.             │
 * │ setAuthStorage                │ No        │ Stores a value in local or session storage.                  │
 * │ deleteAuthSession             │ No        │ Deletes all authentication-related session data.             │
 * │ checkIfUserIsAuthenticated    │ No        │ Checks if the user is authenticated and refreshes tokens.    │
 * └───────────────────────────────┴───────────┴──────────────────────────────────────────────────────────────┘
 */

import { AppLogger } from '../appLogger/';
import {
  generateChallenge,
  generateRandomString,
  launchUri,
  urlSafeEncode,
} from './authClient.helpers';
import { AuthEvents } from './authEvents';
import JWT from './authToken';
import { REFRESH_TOKEN_BUFFER, STORAGE_PREFIX } from './constants';
import { defaultLocale } from '../localisation/helpLinks/helpLinks.constants';
import { setGlobalContext } from '../dataDog/dataDogClient';
import authFetch from './authFetch';

type RegistrationResponse = {
  client_id?: string;
  error_description?: string;
};

type TokenResponse = {
  // access_token: string; unused as it's same as id_token
  id_token: string;
  refresh_token: string;
  token_type: string;
};

type RefreshTokenResponse = {
  AuthenticationResult: {
    IdToken: string;
    AccessToken: string;
    TokenType: string;
  };
  ChallengeParameters: any;
};
type ErrorHandlerCallBackOptions = {
  status?: number;
  errorMessage?: string;
  errorDetails?: any;
  url?: string;
  requestParams?: any;
  shouldThrow?: boolean;
};

export type TokenTypes = 'x-usl-ott' | 'x-usl-deeplink';


class AuthClient {
  // Singleton pattern - only one instance of AuthClient can exist
  private static instance: AuthClient | null = null;

  private _clientId: string = ''; // Initialize _clientId property
  private _codeVerifier: string = ''; // PKCE code verifier
  private _idToken: JWT | undefined; // ID token
  private _refreshToken: string = ''; // Refresh token
  private _state: string = ''; // State value for CSRF protection
  private _customState: string = ''; // Custom state to be passed to the authorization server e.g subdomains
  private _codeChallenge: string = ''; // PKCE code challenge
  private _code: string = ''; // The authorization code is a temporary credential that is exchanged for an id token after login.
  private _tokenRefreshPromise: Promise<JWT | null> | null = null; // Token refresh promise

  // public
  public authEndpoint: string = '';
  public redirectUri: string = '';
  public staticConfig: any;
  public lang: string = defaultLocale;
  public environment: string = ''; // Use to build custom user agent info for the request
  public appName: string = ''; // Use to build custom user agent info for the request
  public appVersion: string = ''; // Use to build custom user agent info for the request

  private _hasConfigured: boolean = false; // We need to throw an error if the client is not configured

  private constructor() {
    this.staticConfig = {
      code_challenge_method: 'S256',
      identity_provider: 'COGNITO',
      response_type: 'code',
      scope: 'openid',
    };
  }

  // Getter and setters
  get clientId() {
    return this._clientId || this.getAuthStorage('local', 'clientId');
  }

  set clientId(value: string) {
    this.setAuthStorage('local', 'clientId', value);
    this._clientId = value;
  }

  get codeVerifier() {
    return this._codeVerifier || this.getAuthStorage('session', 'codeVerifier');
  }

  set codeVerifier(value: string) {
    this.setAuthStorage('session', 'codeVerifier', value);
    this._codeVerifier = value;
  }

  get idToken(): JWT | undefined {
    if (this._idToken) {
      return this._idToken;
    }

    if (this.getAuthStorage('local', 'idToken')) {
      return new JWT(this.getAuthStorage('local', 'idToken'));
    }
  }

  set idToken(value: JWT) {
    if (value) {
      this.setAuthStorage('local', 'idToken', value.jwtString);
      this._idToken = value;
    }
  }

  get refreshToken() {
    return this._refreshToken || this.getAuthStorage('local', 'refreshToken');
  }

  set refreshToken(value: string) {
    this.setAuthStorage('local', 'refreshToken', value);
    this._refreshToken = value;
  }

  private setAuthStorage(
    storageType: 'local' | 'session',
    key: string,
    value: string,
  ) {
    const keyName = `${STORAGE_PREFIX}${key}`;

    if (storageType === 'local') {
      localStorage.setItem(keyName, value);
    } else if (storageType === 'session') {
      sessionStorage.setItem(keyName, value);
    }
  }
  private getAuthStorage(storageType: string, key: string): string {
    const keyName = `${STORAGE_PREFIX}${key}`;

    if (storageType === 'local') {
      return localStorage.getItem(keyName) ?? '';
    } else {
      return sessionStorage.getItem(keyName) ?? '';
    }
  }

  /**
   * Handles critical errors by logging them and publishing an event.
   * When shouldThrow is true (default), this function throws an error and doesn't return.
   * When shouldThrow is false, it logs the error but allows execution to continue.
   *
   */
  private handleError({
    errorMessage,
    status,
    errorDetails,
    url,
    requestParams,
    shouldThrow = true,
  }: ErrorHandlerCallBackOptions): never | void {
    AppLogger.error(errorMessage);

    AuthEvents.publish({
      event: 'auth:error',
      data: {
        error: errorMessage,
        status,
        errorDetails,
        url,
        requestParams,
      },
      eventType: 'error',
    });
    if (shouldThrow) {
      throw new Error(errorMessage);
    }
  }

  /**
   * Ensures that the AuthClient is configured before making any requests.
   */
  private _ensureConfigured() {
    if (!this._hasConfigured) {
      this.handleError({
        errorMessage:
          'AuthClient is not configured. Please call configure() first.',
      });
    }
  }

  private _xClientAppInfo() {
    return {
      xClientAppInfo: {
        environment: this.environment,
        appName: this.appName,
        appVersion: this.appVersion,
      },
    };
  }

  /**
   * Register a client with the Oauth2 server
   * @param clientName
   * @param isSSOX
   * @returns { client_id: string, error_description: string }
   */
  async register({
    clientName,
    isSSOX = false, // SS0X = Single Sign On Between Apps}
  }: {
    clientName: string;
    isSSOX?: boolean;
  }): Promise<RegistrationResponse> {
    this._ensureConfigured();
    const url = `${this.authEndpoint}/register`;
    const body = {
      client_name: clientName,
      // The redirect_uri 'usl://sso-between-apps' is a special redirect uri value for SSOX
      redirect_uris: isSSOX ? ['usl://sso-between-apps'] : [this.redirectUri],
      grant_types: ['authorization_code', 'refresh_token'],
      scope: this.staticConfig.scope,
    };

    const response = await authFetch(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
      {
        ...this._xClientAppInfo(),
      },
    );

    const registrationResponseData: RegistrationResponse =
      await response.json();
    const { client_id } = registrationResponseData;
    if (!response.ok) {
      this.handleError({
        errorMessage: `Failed to register client with status ${response.status}`,
        status: response.status,
        errorDetails: registrationResponseData,
      });
    }
    if (client_id) {
      this.clientId = client_id;
      setGlobalContext(client_id);
    }

    return registrationResponseData;
  }

  /**
   * Authorize to the Oauth2 server
   * This will redirect to USL where the user will start the login process
   */
  async authorize({ lang }: { lang?: string } = {}): Promise<void> {
    this._ensureConfigured();
    const generatedState = generateRandomString(true);

    const state = this._customState
      ? `${generatedState}-${urlSafeEncode(this._customState)}`
      : generatedState;

    const codeVerifier = generateRandomString(false, 128);
    const codeChallenge = await generateChallenge(codeVerifier);

    this._codeChallenge = codeChallenge;
    this.codeVerifier = codeVerifier;
    this._state = state;
    let effectiveLang = {};
    if (lang) {
      const formattedLang = lang.replace('-', '_');
      this.lang = formattedLang;
      effectiveLang = { lang: formattedLang };
    }

    const queryString = Object.entries({
      client_id: this.clientId,
      code_challenge: codeChallenge,
      redirect_uri: this.redirectUri,
      ...effectiveLang,
      state: state,
      ...this.staticConfig,
    })
      .map(([k, v]: any) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');

    try {
      const URL = `${this.authEndpoint}/oauth2/authorize?${queryString}`;

      if (URL) {
        await launchUri(URL);
      }
    } catch (e) {
      this.handleError({
        status: 500,
        errorMessage: `Authorize call failed: ${e}`,
      });
    }
  }

  /**
   * Authorize to the Oauth2 server with a token
   * This is used to authorize the user without the need for a password
   * For example when the user wants to move from Zeus to Hermes without logging in again
   * @param options.token The token to authorize with
   * @param options.isOTT {@deprecated} Whether the token is a One Time Token. This parameter is deprecated, use tokenType instead.
   * @param options.tokenType The type of token ('x-usl-ott' or 'x-usl-deeplink')
   * @returns { code: string, tenancy_hash: string[] }
   */
  /**
   * @deprecated Use the overload with tokenType parameter instead
   */
  async authorizeWithToken(options: { token: string; isOTT: boolean }): Promise<{ code: string; tenancy_hash: string[] }>;
  async authorizeWithToken(options: { token: string; tokenType: TokenTypes }): Promise<{ code: string; tenancy_hash: string[] }>;
  async authorizeWithToken({
    token,
    isOTT,
    tokenType,
  }: {
    token: string;
    isOTT?: boolean;
    tokenType?: TokenTypes;
  }): Promise<{ code: string; tenancy_hash: string[] }> {
    this._ensureConfigured();
    const codeVerifier = generateRandomString(false, 128);
    const codeChallenge = await generateChallenge(codeVerifier);

    this._codeChallenge = codeChallenge;
    this.codeVerifier = codeVerifier;
    this.redirectUri = 'usl://sso-between-apps';

    let effectiveTokenType: string | undefined;
    if (tokenType) {
      // If tokenType is provided, use it directly
      effectiveTokenType = tokenType;
    } else if (isOTT !== undefined) {
      // Fall back to the isOTT boolean if tokenType is not provided
      effectiveTokenType = isOTT ? 'x-usl-ott' : 'x-usl-deeplink';
    } else {
      // If neither is provided, throw an error
      this.handleError({
        errorMessage: 'isOTT or tokenType must be provided',
      }) as never; 
    }

    const body = {
      client_id: this.clientId,
      code_challenge: codeChallenge,
      code_challenge_method: this.staticConfig.code_challenge_method,
      token: token,
      token_type: effectiveTokenType, // API expects 'token_type' as the parameter name
      scope: this.staticConfig.scope,
    };

    const url = `${this.authEndpoint}/oauth2/authorize`;

    const response = await authFetch(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
      {
        ...this._xClientAppInfo(),
      },
    );

    try {
      const authorizeWithOTTResponseData = await response.json();
      const { code, tenancy_hash } = authorizeWithOTTResponseData;
      if (code) {
        this._code = code;
      }

      if (!response.ok) {
        return this.handleError({
          errorMessage: `Failed to exchange code for token ${response.status}`,
          status: response.status,
          url: url,
        }) as never;
      }

      return { code, tenancy_hash };
    } catch (e) {
      return this.handleError({
        errorMessage: `Failed to exchange code for token`,
      }) as never;
    }
  }

  /**
   * Calls register and authorize in sequence
   * @param clientName
   * @param isSSOX
   */
  async registerAndAuthorize({
    clientName,
    isSSOX = false,
    lang,
  }: {
    clientName: string;
    isSSOX?: boolean;
    lang?: string;
  }): Promise<void> {
    this._ensureConfigured();

    // No need for another try and catch as the register and authorize methods will throw an error if it fails
    await this.register({ clientName, isSSOX });

    await this.authorize({ lang: lang || this.lang });
  }

  /**
   * Perform a series of checks to determine if the user is authenticated
   * // 1. If we have a code in the URL or the code is already set in the AuthClient class, exchange it for a token
   * // 2. If we don't have a code in the URL, check if we have a refresh token and idToken and refresh if necessary
   * // 3. forceRefresh: If there is a refresh token and forceRefresh is true or no idToken, refresh/ get a new id token
   * // 4. If we don't have a code in the URL and we don't have tokens in storage, do nothing
   * @param authEndpoint
   * @returns
   */
  async checkIfUserIsAuthenticated(
    forceRefreshIdToken: boolean = false,
  ): Promise<boolean> {
    this._ensureConfigured();

    AuthEvents.publish({
      event: 'auth:checkIfUserIsAuthenticated',
      data: {
        authEndpoint: this.authEndpoint,
      },
    });

    const params = new URLSearchParams(new URL(window.location.href).search);
    const paramCode = params.get('code');
    const effectiveCode = paramCode || this._code;
    this._code = effectiveCode;

    // 1. If we have a code in the URL, exchange it for a token
    if (effectiveCode) {
      AuthEvents.publish({
        event: 'auth:info',
        data: 'Code found in URL, exchanging for token',
      });
      try {
        const tokenResponse: TokenResponse =
          await this.exchangeCodeForToken(effectiveCode);

        if (tokenResponse) {
          AuthEvents.publish({
            event: 'auth:info',
            data: 'Code exchanged for token',
          });
          const { id_token, refresh_token } = tokenResponse;

          this.setAuthStorage('local', 'idToken', id_token);
          this.setAuthStorage('local', 'refreshToken', refresh_token);

          AuthEvents.publish({
            event: 'auth:signIn',
            data: { tokenResponse },
          });
          return true;
        }
      } catch (e) {
        AppLogger.error(`Failed to exchange code for token: ${e}`);
        return false;
      }
    } else if (this.refreshToken && this.idToken) {
      AuthEvents.publish({
        event: 'auth:info',
        data: 'Refresh and IDToken found, calling smartRefresh',
      });
      // 2. If we don't have a code in the URL, check if we have a refreshToken in storage and refresh if necessary
      const idToken = await this.smartRefresh();
      return !!idToken;
    } else if (this.refreshToken && (forceRefreshIdToken || !this.idToken)) {
      // 3. forceRefresh: If there is a refresh token and forceRefresh is true or no idToken, refresh/ get a new id token
      AuthEvents.publish({
        event: 'auth:info',
        data: 'Refresh and IDToken found,, forceRefreshIdToken is true, calling refreshIdToken',
      });
      const idToken = await this.refreshIdToken();
      return !!idToken;
    }
    // 4. If we don't have a code in the URL and we don't have tokens in storage
    return false;
  }

  /**
   * Attempts to refresh the ID token if it is about to expire.
   *
   * This method checks if both the `idToken` and `refreshToken` are available. If the `idToken` is
   * nearing expiration (using a buffer time), it will attempt to refresh the token using the
   * `refreshIdToken` method. If the token is still valid, it returns the existing `idToken`.
   *
   * If no tokens are available, it returns `null`, allowing the caller to handle the absence of valid tokens.
   *
   * @returns {Promise<JWT | null>} The refreshed or existing ID token, or `null` if no tokens are available.
   * @throws Will throw an error if the token expiration time cannot be determined or if the refresh process fails.
   */
  public async smartRefresh(): Promise<JWT | null> {
    this._ensureConfigured();
    let tokenToReturn: JWT | null = null;
    try {
      // Check if we have an idToken or refreshToken in storage

      if (this.refreshToken && this.idToken) {
        let isIdTokenExpiringSoon = false;

        const exp = this.idToken?.getExpiration();

        if (!exp) {
          return this.handleError({
            errorMessage: 'Failed to get the expiration time of the token',
          }) as never;
        }

        // Subtract 30 seconds from the expiry time to create a buffer
        // This will allow us to refresh the token before it expires
        const adjustedExpiryTime = exp - REFRESH_TOKEN_BUFFER;

        // We consider the token expired if the current time is greater than the adjusted expiry time
        if (Date.now() >= adjustedExpiryTime) {
          isIdTokenExpiringSoon = true;
        }

        if (isIdTokenExpiringSoon) {
          // If the token is about to expire, refresh it
          const newIdToken = await this.refreshIdToken();
          tokenToReturn = newIdToken;
        } else {
          // If the token is not about to expire, resolve with the existing token
          tokenToReturn = this.idToken;
        }
        return tokenToReturn;
      } else {
        // In the case of no tokens we return null continuing the flow
        return null;
      }
    } catch (error) {
      return this.handleError({
        errorMessage: `Error in smartRefresh: ${error}`,
      }) as never;
    }
  }

  /**
   * Makes a call to the /token endpoint to exchange the code for a token
   * This is the final step in the OAuth2 flow
   * @param code
   * @returns
   */
  async exchangeCodeForToken(code: string): Promise<TokenResponse> {
    this._ensureConfigured();
    AuthEvents.publish({
      event: 'auth:exchangeCodeForToken',
      data: { code },
    });

    const url = `${this.authEndpoint}/oauth2/token`;
    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('code', code);
    params.append('redirect_uri', this.redirectUri);
    params.append('client_id', this.clientId ?? '');
    params.append('code_verifier', this.codeVerifier);

    const response = await authFetch(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      },
      {
        ...this._xClientAppInfo(),
      },
    );

    const tokenResponseData: TokenResponse = await response.json();
    const { id_token, refresh_token } = tokenResponseData;

    this.idToken = new JWT(id_token);
    this.refreshToken = refresh_token;

    if (!response.ok) {
      this.handleError({
        errorMessage: `Failed to exchange code for token ${response.status}`,
        status: response.status,
        url: url,
      });
    }
    return tokenResponseData;
  }

  /**
   * Once authenticated the idToken will be refreshed periodically (every 5 minutes)
   * The refreshToken lives for 90 days (the TTL be configured in Arch/Aura based on company settings)
   * @returns
   */
  public async refreshIdToken(): Promise<JWT | null> {
    this._ensureConfigured();
    if (!this._tokenRefreshPromise) {
      this._tokenRefreshPromise = (async (): Promise<JWT | null> => {
        try {
          const url = `${this.authEndpoint}/`;
          const headers = {
            'cache-control': 'no-store',
            'content-type': 'application/x-amz-json-1.1',
            'x-amz-target': 'AWSCognitoIdentityProviderService.InitiateAuth',
            'x-amz-user-agent': 'aws-amplify/5.0.4 auth framework/0',
          };
          const body = {
            ClientId: this.clientId,
            AuthFlow: 'REFRESH_TOKEN_AUTH',
            AuthParameters: {
              REFRESH_TOKEN: this.refreshToken,
              DEVICE_KEY: null,
            },
          };

          const response = await authFetch(
            url,
            {
              method: 'POST',
              headers: headers,
              body: JSON.stringify(body),
            },
            {
              ...this._xClientAppInfo(),
            },
          );

          if (!response.ok) {
            return this.handleError({
              errorMessage: `HTTP error! status: ${response.status}`,
              status: response.status,
            }) as never;
          }

          const tokenRefreshData: RefreshTokenResponse = await response.json();

          const responseIdToken =
            tokenRefreshData?.AuthenticationResult?.IdToken;

          if (!responseIdToken) {
            return this.handleError({
              errorMessage: 'AuthenticationResult or IdToken is missing',
            }) as never;
          }

          const newIdToken = new JWT(responseIdToken);

          this.idToken = newIdToken;
          return newIdToken;
        } catch (error) {
          this._tokenRefreshPromise = null;
          this.deleteAuthSession();
          return this.handleError({
            errorMessage: `Couldn't refresh IdToken: ${error}`,
          }) as never;
        } finally {
          this._tokenRefreshPromise = null;
        }
      })();
    }
    return this._tokenRefreshPromise;
  }

  /**
   * Signs the user out and clears the session
   * This method:
   * 1. Calls the logout endpoint
   * 2. Deletes all auth-related data from storage
   * 3. Resets all in-memory properties of the AuthClient instance
   */
  public async signOut() {
    if (this.clientId && this.redirectUri) {
      const logoutUrl = new URL(`${this.authEndpoint}/logout`);
      logoutUrl.searchParams.append('client_id', this.clientId);
      logoutUrl.searchParams.append('logout_uri', this.redirectUri);

      // Always delete the auth session and reset state, even if the logout request fails
      this.deleteAuthSession();
      this.resetAuthClientState();
      
      try {
        const response = await authFetch(
          logoutUrl.toString(),
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
          {
            ...this._xClientAppInfo(),
          },
        );

        if (!response.ok) {
          // Log the error but don't throw - we want to continue with logout flow
          this.handleError({
            errorMessage: `Failed to sign out: ${response.status}`,
            status: response.status,
            url: logoutUrl.toString(),
            shouldThrow: false,
          });
        }
      } catch (e) {
        this.handleError({
          errorMessage: `Failed to sign out: ${e}`,
          shouldThrow: false,
        });
      }
    } else {
      // Even if we don't have clientId and redirectUri, we should still clean up
      this.deleteAuthSession();
      this.resetAuthClientState();
    }
  }

  /**
   * Deletes all authentication-related session data from local and session storage.
   * This includes the idToken, refreshToken, and any other keys that start with '_auth'.
   */
  public deleteAuthSession() {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('_auth')) {
        localStorage.removeItem(key);
      }
    }

    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('_auth')) {
        sessionStorage.removeItem(key);
      }
    }
  }

  /**
   * Resets all in-memory properties of the AuthClient instance to their initial values.
   * This ensures that no authentication data remains in memory after signout.
   */
  private resetAuthClientState() {
    this._clientId = '';
    this._codeVerifier = '';
    this._idToken = undefined;
    this._refreshToken = '';
    this._state = '';
    this._customState = '';
    this._codeChallenge = '';
    this._code = '';
    this._tokenRefreshPromise = null;
  }

  /**
   * Before using the AuthClient, it has to be configure it with the authEndpoint and redirectUri
   * The authEndpoint is the URL of the Oauth2 server ie. https://global.enterprise.test.lifeworks.com
   * The redirectUri is the URL to redirect to after the user has logged in
   * @param { authEndpoint: string, redirectUri: string }
   */
  public configure({
    authEndpoint,
    redirectUri,
    environment,
    appName,
    appVersion,
  }: {
    authEndpoint: string;
    redirectUri?: string;
    environment: string;
    appName: string;
    appVersion: string;
  }) {
    if (!authEndpoint) {
      return this.handleError({
        errorMessage: 'authEndpoint is required',
      }) as never;
    }

    // Default the redirectUri to the current window location
    // For Next.js we need explicitly check if windows is defined as it can cause an error in the build process
    if (!redirectUri && typeof window !== 'undefined' && window.location) {
      const newUri = window.location.protocol + '//' + window.location.host;
      this.redirectUri = newUri;

      AppLogger.info(
        'AuthClient.redirectUri not configured with falling back to default: ',
        newUri,
      );
    } else if (redirectUri) {
      this.redirectUri = redirectUri;
    }

    this.environment = environment;
    this.appName = appName;
    this.appVersion = appVersion;

    this.authEndpoint = authEndpoint;
    this._hasConfigured = true;
  }

  /**
   * Singleton pattern - only one instance of AuthClient can exist
   * @returns { AuthClient }
   */
  public static getInstance(): AuthClient {
    if (!AuthClient.instance) {
      AuthClient.instance = new AuthClient();
    }
    return AuthClient.instance;
  }
}
// Create a single instance of Oauth2Client and export it
const oauth2Client = AuthClient.getInstance();

export { oauth2Client as AuthClient };
